/**
 * « Ajuster le bloc au contenu » — équivalent du Fit Frame to Content
 * d'InDesign (Objet → Ajustement) : resserre le cadre de sélection sur les
 * dimensions réelles de ce qu'il contient.
 *
 * - Texte : mesure DOM du rendu réel (mêmes repères que TextOverflowIndicator :
 *   contenu rendu à TEXT_SCALE=10, converti en mm via canvas.scale). La largeur
 *   vient de la plus longue ligne effective (Range sur chaque paragraphe).
 * - Image : ratio naturel du fichier — la largeur du cadre est conservée, la
 *   hauteur s'aligne sur le ratio (fin des bandes vides de l'object-contain).
 * - Code-barres : idem, ratio du canvas généré par bwip-js.
 * - Formes/symboles : le contenu épouse déjà le cadre — rien à ajuster.
 */

const TEXT_SCALE = 10; // cf. TextLayer.jsx

export function canFitFrame(el) {
    return ['text', 'image', 'barcode'].includes(el?.type);
}

/** Mesure la taille du contenu en mm. Retourne {width, height} ou null. */
export async function measureContent(el, canvasScale) {
    if (!el || !canvasScale) return null;

    if (el.type === 'text') {
        // Rotation : les rectangles mesurés seraient ceux de la boîte tournée
        // à l'écran — mesure fausse. On ne traite que les blocs non tournés.
        if (el.rotation && Math.abs(el.rotation % 360) > 0.01) return null;
        const container = document.querySelector(`[data-text-container="${el.id}"]`);
        const inner = container?.querySelector('[data-text-inner]');
        if (!inner) return null;
        // Les couches de rendu (contour + remplissage) sont en position:absolute
        // → scrollHeight ne voit RIEN. On mesure le rectangle englobant des
        // lignes réellement rendues via un Range sur la couche de remplissage
        // (dernier enfant, z-index 2). getBoundingClientRect renvoie des px
        // ÉCRAN, transform scale(0.1) déjà appliqué → mm = px / canvasScale.
        const body = inner.lastElementChild;
        if (!body) return null;
        const range = document.createRange();
        range.selectNodeContents(body);
        const rect = range.getBoundingClientRect();
        if (!rect.width || !rect.height) return null;
        const contRect = container.getBoundingClientRect();

        // Le texte n'est PAS posé à une position absolue : il est ANCRÉ au cadre.
        // Aligné à gauche il colle au bord gauche, à droite au bord droit, centré
        // au milieu — et toujours en haut. Déplacer le cadre déplace donc le
        // texte avec lui. C'est ce qu'oubliait le calcul précédent, qui reculait
        // le cadre de la marge : le texte reculait d'autant, et un « ajuster au
        // contenu » sur un texte aligné à gauche le décalait de 4 px vers la
        // gauche à chaque appel (banc-ajustement, 2026-08-21).
        //
        // On raisonne donc en écran, en offsets DEPUIS le coin du cadre, et on
        // choisit le nouveau cadre pour que les glyphes ne bougent pas d'un pixel.
        const gG = rect.left - contRect.left;      // bord gauche des glyphes
        const gD = rect.right - contRect.left;     // bord droit des glyphes
        const gB = rect.bottom - contRect.top;     // bas des glyphes
        const L0 = contRect.width;

        // 2 % de marge PAR CÔTÉ LIBRE : les polices décoratives (pleins/déliés,
        // swashes) peignent légèrement au-delà des boîtes de ligne, et le cadre
        // découpe (overflow: hidden). Du côté où le texte est ancré, la marge ne
        // peut pas exister — le texte y colle par construction — donc on ne la
        // compte pas : un texte aligné à gauche recevait sinon 4 % de vide à
        // droite, soit près de 2 mm sur une mention de 45 mm.
        const padW = rect.width * 0.02;
        const padH = rect.height * 0.02;

        const align = el.textAlign || 'left';
        let largeur;  // largeur du nouveau cadre, en px écran
        let dxPx;     // déplacement du bord gauche du cadre, en px écran
        if (align === 'center') {
            // Deux côtés libres : une marge de chaque côté.
            largeur = (gD - gG) + padW * 2;
            dxPx = (gG + gD) / 2 - largeur / 2;
        } else if (align === 'right') {
            // Ancré à droite : le bord droit du cadre ne bouge pas, marge à gauche.
            largeur = (L0 - gG) + padW;
            dxPx = L0 - largeur;
        } else {
            // Gauche et justifié : le bord gauche du cadre ne bouge pas, marge à
            // droite. Pour du justifié, réduire la largeur re-répartirait les
            // blancs — on ne descend jamais sous la largeur occupée, ce que gD
            // garantit.
            largeur = gD + padW;
            dxPx = 0;
        }

        return {
            width: Math.max(2, largeur / canvasScale),
            // Ancrage vertical : toujours en haut. La hauteur part du HAUT DU
            // CADRE (et non du haut des glyphes) pour englober le demi-interligne
            // de la première ligne, sans quoi le rejeu du calcul rognerait un peu
            // plus à chaque fois.
            height: Math.max(2, (gB + padH) / canvasScale),
            dx: dxPx / canvasScale,
            dy: 0,
        };
    }

    if (el.type === 'image') {
        if (!el.url) return null;
        const ratio = await new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(img.naturalHeight / img.naturalWidth || null);
            img.onerror = () => resolve(null);
            img.src = el.url;
        });
        if (!ratio) return null;
        // Largeur conservée, hauteur alignée sur le ratio du fichier
        return { width: el.width, height: Math.max(2, el.width * ratio) };
    }

    if (el.type === 'barcode') {
        const cnv = document.getElementById(`barcode-canvas-${el.id}`);
        if (!cnv || !cnv.width || !cnv.height) return null;
        return { width: el.width, height: Math.max(2, el.width * (cnv.height / cnv.width)) };
    }

    return null;
}

/**
 * Applique l'ajustement sur tous les éléments sélectionnés éligibles.
 * Retourne le nombre d'éléments ajustés.
 */
export async function fitSelectionToContent(store) {
    const { selectedElementIds, elements, canvas, updateElement, _pushState } = store;
    let count = 0;
    for (const id of selectedElementIds) {
        const el = elements.find((e) => e.id === id);
        // Même règle que `eligible` de fitElementsToContent, qui est locale à
        // cette fonction-là : l'appeler ici levait une ReferenceError, avalée
        // par la promesse — le bouton ne faisait rien, sans un mot.
        if (!canFitFrame(el) || el.locked) continue;
        const size = await measureContent(el, canvas.scale);
        if (!size) continue;
        // Seuil : on ne touche pas si la différence est < 0,1 mm (déjà ajusté)
        const moved = Math.abs(size.dx || 0) > 0.05 || Math.abs(size.dy || 0) > 0.05;
        if (!moved && Math.abs(size.width - el.width) < 0.1 && Math.abs(size.height - el.height) < 0.1) continue;
        const patch = { width: size.width, height: size.height };
        if (size.dx !== undefined) { patch.x = el.x + size.dx; patch.y = el.y + size.dy; }
        updateElement(id, patch);
        count += 1;
    }
    if (count > 0 && typeof _pushState === 'function') {
        setTimeout(() => _pushState('Ajuster au contenu'), 0);
    }
    return count;
}

/* ────────────────────────────────────────────────────────────────────────────
   Ajustement AUTOMATIQUE, après une insertion de calques.

   Mesurer exige que le calque soit déjà peint : `measureContent` lit le DOM
   (`[data-text-container]`) et rend `null` tant que React n'a pas commité.
   D'où l'attente en deux temps ci-dessous — les images d'animation pour le
   rendu, `document.fonts.ready` pour la police. Sans cette seconde attente, un
   calque en police téléversée est mesuré dans la police de repli et le cadre
   sort à une largeur qui n'est celle de personne.
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Deux images d'affichage : la première laisse React commiter, la seconde
 * garantit que le layout du commit est calculé.
 *
 * Doublé d'une minuterie, parce que `requestAnimationFrame` NE SE DÉCLENCHE PAS
 * dans un onglet caché : sans ce garde-fou, un import lancé puis laissé de côté
 * (l'utilisateur passe à une autre fenêtre pendant les secondes de l'appel API)
 * attendait une image qui ne venait jamais, et l'ajustement ne se posait pas —
 * silencieusement. Le premier des deux qui répond gagne.
 */
const DELAI_IMAGE_MS = 50;

function deuxImages() {
    return new Promise((resolve) => {
        const minuterie = setTimeout(resolve, DELAI_IMAGE_MS);
        const fini = () => { clearTimeout(minuterie); resolve(); };
        if (typeof requestAnimationFrame !== 'function') return;
        requestAnimationFrame(() => requestAnimationFrame(fini));
    });
}

/** N'échoue jamais, et n'attend pas indéfiniment : une police qui ne charge
 *  jamais (réseau coupé, feuille distante muette) laisse `document.fonts.ready`
 *  en attente pour toujours. Sans le plafond ci-dessous, l'ajustement ne se
 *  poserait alors JAMAIS, sans un message — le pire des échecs. Au-delà du
 *  plafond on mesure ce qui est peint, quitte à mesurer une police de repli. */
const PLAFOND_POLICES_MS = 2000;

async function attendrePolices() {
    try {
        if (typeof document === 'undefined' || !document.fonts || !document.fonts.ready) return;
        await Promise.race([
            document.fonts.ready,
            new Promise((resolve) => setTimeout(resolve, PLAFOND_POLICES_MS)),
        ]);
    } catch { /* rendu au repli : on mesure quand même */ }
}

/** Attend que les calques texte visés soient réellement dans le DOM. Renvoie
 *  false si l'attente expire (onglet inactif, calque masqué) — on mesure alors
 *  ce qui est mesurable et on laisse le reste tel quel. */
async function attendreMontage(idsTexte, tentatives = 8) {
    if (idsTexte.length === 0) return true;
    for (let i = 0; i < tentatives; i++) {
        await deuxImages();
        const toutMonte = idsTexte.every(
            (id) => document.querySelector(`[data-text-container="${id}"] [data-text-inner]`)
        );
        if (toutMonte) return true;
    }
    return false;
}

/**
 * Ajuste au contenu une liste de calques DÉSIGNÉS PAR ID, après leur insertion.
 *
 * Contrairement à `fitSelectionToContent`, qui travaille sur la sélection à
 * l'instant du raccourci, celle-ci attend le rendu puis RELIT l'état du store :
 * entre l'appel et la mesure, l'import a pu continuer d'écrire.
 *
 * Tous les cadres sont posés en UNE mutation (`patchElements`) et UN seul point
 * d'annulation : « Importer » puis « Ajuster » ne doivent pas demander deux
 * Ctrl+Z pour revenir en arrière.
 *
 * `types` restreint l'ajustement à certains types de calque. L'import PDF s'en
 * sert pour ne toucher qu'au TEXTE : le fond d'un gabarit est posé au format
 * exact de la page, et lui appliquer le ratio de son image le décalerait.
 *
 * @param {string[]} ids
 * @param {{nomHistorique?: string, historique?: boolean, types?: string[]}} [options]
 * @returns {Promise<number>} nombre de cadres effectivement ajustés
 */
export async function fitElementsToContent(ids, options = {}) {
    const { nomHistorique = 'Ajuster au contenu', historique = true, types = null } = options;
    if (!Array.isArray(ids) || ids.length === 0) return 0;

    const { useLabelStore } = await import('../store/useLabelStore');
    const avant = useLabelStore.getState();
    if (avant.autoFitOnImport === false) return 0;

    const eligible = (el) => canFitFrame(el)
        && (!types || types.includes(el.type))
        && !el.locked;   // un calque verrouillé n'est pas à nous
    const eligibles = ids.filter((id) => eligible(avant.elements.find((e) => e.id === id)));
    if (eligibles.length === 0) return 0;
    const idsTexte = eligibles.filter(
        (id) => avant.elements.find((e) => e.id === id)?.type === 'text'
    );

    await attendrePolices();
    await attendreMontage(idsTexte);

    // Relecture APRÈS l'attente : `elements` et `canvas.scale` ont pu bouger.
    const { elements, canvas, patchElements, _pushState } = useLabelStore.getState();
    const patches = {};
    for (const id of eligibles) {
        const el = elements.find((e) => e.id === id);
        if (!canFitFrame(el)) continue;
        const size = await measureContent(el, canvas.scale);
        if (!size) continue;
        const deplace = Math.abs(size.dx || 0) > 0.05 || Math.abs(size.dy || 0) > 0.05;
        if (!deplace
            && Math.abs(size.width - el.width) < 0.1
            && Math.abs(size.height - el.height) < 0.1) continue;
        const patch = { width: size.width, height: size.height };
        if (size.dx !== undefined) { patch.x = el.x + size.dx; patch.y = el.y + size.dy; }
        patches[id] = patch;
    }

    const n = Object.keys(patches).length;
    if (n > 0) {
        patchElements(patches);
        if (historique && typeof _pushState === 'function') {
            setTimeout(() => _pushState(nomHistorique), 0);
        }
    }
    return n;
}
